import React, { useState, useCallback, useRef } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as MuiLab from "@mui/material";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { styles } from "./style";
import { AppCard } from "component/shared/app-card";

// Sample Market data from the real dataset
const SAMPLE_MARKET_JSON = `[
  {
	"id": "8617",
	"linkName": "newest-homes-for-sale-in-nashville",
	"linkTitle": "Newest Homes for Sale in Nashville",
	"queryString": "page=listings&pt=1&idxStatus=active&city[]=5494&city[]=16896&city[]=31834&city[]=32035&city[]=33091&ccz=city&searchRefinement[]=amin_photocount&amin_photocount=1&per=25&srt=newest",
	"timesViewed": "4",
	"featured": "n",
	"created": "2020-12-30 14:55:00",
	"category": "listings",
	"addedViaApi": "0",
	"lastEdited": "2021-01-04 10:40:52",
	"url": "properties/newest-homes-for-sale-in-nashville",
	"uid": "7"
  },
  {
	"id": "8618", 
	"linkName": "homes-for-sale-with-pools-in-nashville",
	"linkTitle": "Homes for Sale with Pools in Nashville",
	"queryString": "page=listings&pt=1&idxStatus=active&city[]=5494&a_poolprivateyn=y&per=25&srt=newest&color=blue",
	"timesViewed": "2",
	"featured": "y",
	"created": "2020-12-30 15:10:00", 
	"category": "listings",
	"addedViaApi": "0",
	"lastEdited": "2021-01-04 10:45:30",
	"url": "properties/homes-for-sale-with-pools-in-nashville",
	"uid": "7"
  }
]`;

interface DataInputProps extends WithStyles<typeof styles> {
	onDataChange?: (data: string) => void;
	isProcessing?: boolean;
	errors?: string[];
}

const Component: React.FC<DataInputProps> = ({
	classes = {},
	onDataChange,
	isProcessing = false,
	errors = []
}) => {
	const theme = Mui.useTheme();
	const [inputData, setInputData] = useState('');
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Calculate estimated records and validation
	const lineCount = inputData.split('\n').length;
	const estimatedRecords = inputData ? Math.max(0, lineCount - 2) : 0; // Rough estimate for JSON arrays
	
	const validateJson = useCallback((data: string): boolean => {
		if (!data.trim()) return true;
		try {
			JSON.parse(data);
			return true;
		} catch{
			return false;
		}
	}, []);

	const isValidJson = validateJson(inputData);

	const handleInputChange = useCallback((data: string) => {
		setInputData(data);
		onDataChange?.(data);
	}, [onDataChange]);

	const handleSampleData = useCallback(() => {
		handleInputChange(SAMPLE_MARKET_JSON);
	}, [handleInputChange]);

	const handleFileSelect = useCallback((file: File) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const content = e.target?.result as string;
			if (content) {
				handleInputChange(content);
			}
		};
		reader.readAsText(file);
	}, [handleInputChange]);

	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		const files = Array.from(e.dataTransfer.files);
		const file = files.find(f => f.type === 'application/json' || 
			f.name.endsWith('.json') || f.type === 'text/plain');
		
		if (file) {
			handleFileSelect(file);
		}
	}, [handleFileSelect]);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
	}, []);

	return (
		<AppCard className={classes.card}>
			<Mui.CardContent className={classes.cardContent}>
				<Mui.Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Mui.Box display="flex" alignItems="center">
						<FeatherIcon className={classes.headerIcon}>
							<Icons.Upload />
						</FeatherIcon>
						<Mui.Typography variant="h6">
							Data Input (JSON)
						</Mui.Typography>
					</Mui.Box>

					<Mui.Box className={classes.headerActions}>
						{!isValidJson && inputData && (
							<Mui.Chip
								label="Invalid JSON"
								size="small"
								color="secondary"
							/>
						)}
						{isProcessing && (
							<Mui.CircularProgress size={20} />
						)}
					</Mui.Box>
				</Mui.Box>

				{/* Error Display */}
				{errors.length > 0 && (
					<MuiLab.Alert severity="error" className={classes.errorAlert}>
						<ul className={classes.errorList}>
							{errors.slice(0, 3).map((error, index) => (
								<li key={index}>{error}</li>
							))}
							{errors.length > 3 && (
								<li>... and {errors.length - 3} more errors</li>
							)}
						</ul>
					</MuiLab.Alert>
				)}

				{/* Main Input Area */}
				<Mui.Box 
					flex={1} 
					position="relative"
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					className={classes.inputArea}
				>
					<Mui.TextField
						multiline
						fullWidth
						rows={20}
						variant="outlined"
						placeholder={`Paste your Saved Link JSON data here or drag & drop a JSON file...
Example format:
	[{
		"id": "8617",
		"linkTitle": "Market Name",
		"queryString": "page=listings&pt=sfr&city[]=5494",
		"featured": "y"
	}]`
						}
						value={inputData}
						onChange={(e) => handleInputChange(e.target.value)}
						disabled={isProcessing}
						InputProps={{
							className: classes.textField
						}}
						inputProps={{
							className: classes.textFieldInput
						}}
					/>
				</Mui.Box>

				{/* Action Buttons */}
				<Mui.Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
					<Mui.Button
						variant="outlined"
						startIcon={<FeatherIcon><Icons.FileText /></FeatherIcon>}
						onClick={handleSampleData}
						disabled={isProcessing}
					>
						Load Sample JSON
					</Mui.Button>

					<Mui.Button
						variant="outlined"
						startIcon={<FeatherIcon><Icons.Upload /></FeatherIcon>}
						onClick={() => fileInputRef.current?.click()}
						disabled={isProcessing}
					>
						Upload File
					</Mui.Button>
				</Mui.Box>

				{/* Hidden File Input */}
				<input
					ref={fileInputRef}
					type="file"
					accept=".json,.txt"
					className={classes.hiddenFileInput}
					onChange={(e) => {
						const file = e.target.files?.[0];
						if (file) handleFileSelect(file);
					}}
				/>
			</Mui.CardContent>
		</AppCard>
	);
};

export const DataInput = withStyles(Component, styles);